"use client"

import React, { useState } from 'react'
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { MoveLeft, MoveRight } from 'lucide-react'

interface ContentCarouselProps<T> {
    items: T[];
    renderItem: (item: T, index: number) => React.ReactNode;
}

export default function ContentCarousel<T>({ items, renderItem }: ContentCarouselProps<T>) {
    const [desktopApi, setDesktopApi] = useState<CarouselApi>()
    const [mobileApi, setMobileApi] = useState<CarouselApi>()
    const [mobileCurrentSlide, setMobileCurrentSlide] = useState(0)
    const [desktopCanScrollPrev, setDesktopCanScrollPrev] = useState(false)
    const [desktopCanScrollNext, setDesktopCanScrollNext] = useState(true)

    // Desktop carousel effect handlers
    React.useEffect(() => {
        if (!desktopApi) return

        const onSelect = () => {
            setDesktopCanScrollPrev(desktopApi.canScrollPrev())
            setDesktopCanScrollNext(desktopApi.canScrollNext())
        }

        desktopApi.on('select', onSelect)
        onSelect() // Set initial state

        return () => {
            desktopApi.off('select', onSelect)
        }
    }, [desktopApi])

    // Mobile carousel effect handlers
    React.useEffect(() => {
        if (!mobileApi) return

        const onSelect = () => {
            setMobileCurrentSlide(mobileApi.selectedScrollSnap())
        }

        mobileApi.on('select', onSelect)
        onSelect() // Set initial state

        return () => {
            mobileApi.off('select', onSelect)
        }
    }, [mobileApi])

    if (items.length === 0) {
        return null;
    }

    return (
        <>
            {/* Desktop View */}
            <div className="hidden md:block">
                <Carousel
                    className="w-full"
                    setApi={setDesktopApi}
                    opts={{
                        align: "start",
                        slidesToScroll: 1,
                        duration: 30,
                    }}
                >
                    <CarouselContent className="-ml-4">
                        {items.map((item, index) => (
                            <CarouselItem key={index} className="pl-8 basis-1/3">
                                {renderItem(item, index)}
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>
                <div className="mt-8 flex justify-center gap-4">
                    <Button
                        variant="outline"
                        size="icon"
                        className={cn(
                            "relative h-12 rounded-full transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
                            desktopCanScrollPrev ? "w-24" : "w-12"
                        )}
                        disabled={!desktopCanScrollPrev}
                        onClick={() => desktopApi?.scrollPrev()}
                    >
                        <MoveLeft className="size-6" />
                    </Button>
                    <Button
                        variant="outline"
                        className={cn(
                            "relative h-12 rounded-full transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
                            desktopCanScrollNext ? "w-24 px-6" : "w-12 px-0"
                        )}
                        disabled={!desktopCanScrollNext}
                        onClick={() => desktopApi?.scrollNext()}
                    >
                        <MoveRight className="size-6" />
                    </Button>
                </div>
            </div>

            {/* Mobile View */}
            <div className="w-full md:hidden">
                <div className="relative space-y-6 overflow-hidden w-full">
                    <Carousel className="w-full" setApi={setMobileApi}>
                        <CarouselContent>
                            {items.map((item, index) => (
                                <CarouselItem key={index}>
                                    {renderItem(item, index)}
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                    </Carousel>

                    {/* Mobile Navigation Indicators */}
                    <div className="flex justify-center gap-2 px-4">
                        {items.map((_, index) => (
                            <button
                                key={index}
                                type="button"
                                className={cn(
                                    "relative h-1 w-full overflow-hidden rounded-full transition-colors",
                                    index === mobileCurrentSlide ? "bg-primary" : "bg-secondary"
                                )}
                                onClick={() => mobileApi?.scrollTo(index)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </>
    )
}
